// Platforms and customer profiles (mig 0017).
//
//   FRT Customer ──< Customer Platform ──< Customer Profile ──< Account
//
// The profile owns address, phone, email and OTP channels; accounts roll up
// to it. A customer platform's profile_mode decides who a profile belongs to.

import type { ISO8601 } from './primitives.js';
import type { OobEntry, OobInput } from './entities.js';

export type ProfileMode = 'per_tester_multi' | 'per_tester' | 'shared';

export const PROFILE_MODES: { value: ProfileMode; label: string; hint: string }[] = [
  {
    value: 'per_tester',
    label: 'One per tester',
    hint: 'Each tester has their own profile on this platform.',
  },
  {
    value: 'per_tester_multi',
    label: 'Several per tester',
    hint: 'A tester may have more than one profile here, told apart by a label.',
  },
  {
    value: 'shared',
    label: 'Shared',
    hint: 'One profile for the whole platform; each account keeps its own tester.',
  },
];

export const PROFILE_TEXT_MAX = 200;

export const PLATFORM_NAME_MAX = 100;

/** A platform a customer offers (mig 0017): a named instance of a platform
 *  type, with its profile mode. Names are unique per customer (mig 0018);
 *  a customer may offer several platforms of the same type. */
export interface CustomerPlatformRow {
  id: number;
  frt_customer_id: number;
  frt_customer_code: string;
  frt_customer_name: string;
  /** The customer's own name for it, e.g. "Consumer Lending". */
  name: string;
  /** Platform type (Lookups → Platform Types). */
  platform_id: number;
  platform_type_name: string;
  profile_mode: ProfileMode;
  active: boolean;
  profile_count: number;
  created_at: ISO8601;
  updated_at: ISO8601;
}

export interface CustomerPlatformCreate {
  /** Platform type. */
  platform_id: number;
  /** Defaults to the type's name. */
  name?: string | null;
  profile_mode: ProfileMode;
}

export interface CustomerPlatformPatch {
  name?: string;
  /** Refused once the platform has profiles. */
  profile_mode?: ProfileMode;
  active?: boolean;
}

export interface ProfileAddress {
  address_line1: string | null;
  address_line2: string | null;
  city: string | null;
  state: string | null;
  postal_code: string | null;
  country: string | null;
}

export interface CustomerProfileRow extends ProfileAddress {
  id: number;
  customer_platform_id: number;
  frt_customer_id: number;
  frt_customer_code: string;
  frt_customer_name: string;
  /** Platform type. */
  platform_id: number;
  platform_type_name: string;
  /** The customer platform's name, e.g. "Consumer Lending". */
  platform_name: string;
  profile_mode: ProfileMode;
  /** Null for shared profiles. */
  tester_id: number | null;
  tester_display: string | null;
  label: string | null;
  /** "ALLY · Personal · JerryT · Main" */
  display: string;
  phone_id: number | null;
  /** "+1 5551234567" */
  phone_display: string | null;
  email: string | null;
  /** One line per OTP channel; '; '-separated. */
  oob_summary: string | null;
  notes: string | null;
  active: boolean;
  account_count: number;
  created_at: ISO8601;
  updated_at: ISO8601;
}

export interface CustomerProfileDetail extends CustomerProfileRow {
  oob_entries: OobEntry[];
  accounts: { id: number; display_name: string; account_status: string }[];
}

export interface CustomerProfileUpsert extends Partial<ProfileAddress> {
  customer_platform_id: number;
  /** Required unless the platform is shared (then must be null/absent). */
  tester_id?: number | null;
  /** Required for per_tester_multi; optional otherwise. */
  label?: string | null;
  phone_id?: number | null;
  email?: string | null;
  /** Replaces the profile's OTP channels wholesale. */
  oob_entries?: OobInput[];
  notes?: string | null;
  active?: boolean;
}

export interface CustomerProfileListQuery {
  frt_customer_id?: number;
  customer_platform_id?: number;
  platform_id?: number;
  tester_id?: number;
  q?: string;
  /** 'true' (default) = active only, 'false' = inactive only, 'all' */
  active?: 'true' | 'false' | 'all';
}

export function formatAddress(a: Partial<ProfileAddress>): string {
  const cityLine = [a.city, [a.state, a.postal_code].filter(Boolean).join(' ')]
    .filter(Boolean)
    .join(', ');
  return [a.address_line1, a.address_line2, cityLine, a.country].filter(Boolean).join(', ');
}

/** "ALLY · Consumer Lending · JerryT · Main" (platform = the customer
 *  platform's name) — shared profiles show "Shared". */
export function profileDisplay(p: {
  frt_customer_code: string;
  platform_name: string;
  tester_display: string | null;
  label: string | null;
}): string {
  return [p.frt_customer_code, p.platform_name, p.tester_display ?? 'Shared', p.label]
    .filter(Boolean)
    .join(' · ');
}

/** "Consumer Lending (Personal)", or just "Personal" when the customer kept
 *  the type's name (mig 0018). */
export function platformLabel(p: { name: string; type: string }): string {
  return p.name.toLowerCase() === p.type.toLowerCase() ? p.name : `${p.name} (${p.type})`;
}
